import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useQuery } from '@tanstack/react-query';
import {
  cascadeShowTimeline,
  detectLineupConflicts,
  type LineupDanceWithCast,
  type DancerFamilyRef,
} from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import {
  useShowLineup,
  useDances,
  useSetLineupOrder,
  useAddDanceToLineup,
  useRemoveDanceFromLineup,
} from '@/data/productions';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { SortableLineupItem } from '@/components/lineup/SortableLineupItem';
import { CastModal } from '@/components/lineup/CastModal';
import { useRecentLineupRemovals, useRestoreLastRemoval } from '@/data/conflicts';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/productions/$id/shows/$showId')({
  component: ShowDetail,
});

function useShow(showId: string) {
  return useQuery({
    queryKey: ['show', showId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shows')
        .select('*')
        .eq('id', showId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useAllCastForShow(danceIds: string[]) {
  return useQuery({
    enabled: danceIds.length > 0,
    // Defensive copy — Array.prototype.sort mutates in place. The current
    // call site passes a fresh array, but the pattern is fragile if it's
    // ever called with a borrowed reference.
    queryKey: ['cast-for-show', [...danceIds].sort().join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cast_assignments')
        .select('dance_id, dancer_id, dancers(id, first_name, last_name, family_id)')
        .in('dance_id', danceIds);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function ShowDetail() {
  const { id: productionId, showId } = Route.useParams();
  const show = useShow(showId);
  const lineup = useShowLineup(showId);
  const dances = useDances(productionId);
  const setOrder = useSetLineupOrder();
  const addToLineup = useAddDanceToLineup();
  const removeFromLineup = useRemoveDanceFromLineup();

  const lineupDanceIds = (lineup.data ?? []).map((l) => l.dance_id);
  const castQuery = useAllCastForShow(lineupDanceIds);

  // Sortable order state — drives the dnd-kit container. Initialized from
  // the server lineup and updated optimistically on drag end.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  useEffect(() => {
    if (lineup.data) {
      setOrderedIds(lineup.data.map((l) => l.dance_id));
    }
  }, [lineup.data]);

  // Sensors: pointer (mouse, touch) + keyboard with explicit
  // sortableKeyboardCoordinates — non-negotiable per the a11y plan.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Cast lookup
  const castByDance = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of (castQuery.data ?? []) as { dance_id: string; dancer_id: string }[]) {
      const list = m.get(r.dance_id) ?? [];
      list.push(r.dancer_id);
      m.set(r.dance_id, list);
    }
    return m;
  }, [castQuery.data]);

  // Build the cascade input + run the pure function for live preview.
  const dancesById = useMemo(
    () => new Map((dances.data ?? []).map((d) => [d.id, d])),
    [dances.data],
  );
  const cascadeInput = useMemo(() => {
    return orderedIds
      .map((danceId, i) => {
        const d = dancesById.get(danceId);
        if (!d) return null;
        return {
          dance_id: danceId,
          sequence_index: i + 1,
          length_seconds: d.length_seconds,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [orderedIds, dancesById]);

  const cascade = useMemo(() => {
    if (!show.data) return [];
    return cascadeShowTimeline(
      {
        performance_at: show.data.performance_at,
        default_gap_seconds: show.data.default_gap_seconds,
        intermission_after_sequence: show.data.intermission_after_sequence,
        intermission_minutes: show.data.intermission_minutes,
      },
      cascadeInput,
    );
  }, [show.data, cascadeInput]);

  // Dancer family map for sibling-conflict detection. Build from cast query.
  const dancerFamily = useMemo<Map<string, DancerFamilyRef>>(() => {
    const m = new Map<string, DancerFamilyRef>();
    for (const r of (castQuery.data ?? []) as {
      dancer_id: string;
      dancers: { id: string; first_name: string; last_name: string; family_id: string } | null;
    }[]) {
      const d = r.dancers;
      if (!d) continue;
      m.set(d.id, {
        dancer_id: d.id,
        family_id: d.family_id,
        display_name: `${d.first_name} ${d.last_name}`,
      });
    }
    return m;
  }, [castQuery.data]);

  const lineupWithCast: LineupDanceWithCast[] = cascade.map((c) => ({
    dance_id: c.dance_id,
    sequence_index: c.sequence_index,
    computed_start_time: c.computed_start_time,
    computed_end_time: c.computed_end_time,
    cast_dancer_ids: castByDance.get(c.dance_id) ?? [],
  }));

  const conflicts = useMemo(
    () => detectLineupConflicts(lineupWithCast, dancerFamily),
    [lineupWithCast, dancerFamily],
  );
  const conflictedDanceIds = useMemo(() => {
    const s = new Set<string>();
    for (const c of conflicts) for (const id of c.dance_ids) s.add(id);
    return s;
  }, [conflicts]);

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(next); // optimistic
    try {
      await setOrder.mutateAsync({
        show_id: showId,
        ordering: next.map((danceId, i) => ({ dance_id: danceId, sequence_index: i + 1 })),
      });
    } catch (err) {
      toast({
        title: "Couldn't save order",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
      // Revert by reloading from server.
      setOrderedIds((lineup.data ?? []).map((l) => l.dance_id));
    }
  }

  const dancesNotInLineup = (dances.data ?? []).filter((d) => !orderedIds.includes(d.id));

  const [castModalDanceId, setCastModalDanceId] = useState<string | null>(null);
  const castModalDance = castModalDanceId ? dancesById.get(castModalDanceId) : null;

  if (show.isLoading) return <p aria-live="polite">Loading…</p>;
  if (!show.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Show not found.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Production
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1>{show.data.name}</h1>
            <p className="mt-1 text-[var(--color-ink-muted)]">
              {new Date(show.data.performance_at).toLocaleString()}
            </p>
          </div>
          <UndoButton showId={showId} />
        </div>
      </header>

      {conflicts.length > 0 && (
        <aside
          role="alert"
          aria-labelledby="lineup-conflicts-heading"
          className="surface space-y-2 border border-[var(--color-warn)] bg-[var(--color-warn-muted)] p-5"
        >
          <h2 id="lineup-conflicts-heading" className="font-medium text-[var(--text-base)]">
            {conflicts.length} lineup {conflicts.length === 1 ? 'warning' : 'warnings'}
          </h2>
          <ul className="space-y-1.5 text-[var(--text-sm)]">
            {conflicts.map((c, i) => (
              <li key={`${c.kind}-${i}`} className="flex flex-wrap gap-2">
                <Badge
                  tone={
                    c.severity === 'high' ? 'danger' : c.severity === 'medium' ? 'warn' : 'info'
                  }
                >
                  {c.kind}
                </Badge>
                <span>{c.message}</span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <section aria-labelledby="lineup-heading">
        <h2 id="lineup-heading" className="text-[var(--text-xl)]">
          Running order
        </h2>
        {/* Visually-hidden hint that each drag handle's aria-describedby
            points to. Screen reader users hear the keyboard interaction
            model when they focus the handle. */}
        <span id="dnd-kb-hint" className="sr-only">
          Press Space to pick up, arrow keys to move, Space again to drop.
        </span>

        {orderedIds.length === 0 ? (
          <Empty
            title="No dances in this show yet"
            description="Add a dance from the picker below to start building the running order."
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => void handleDragEnd(event)}
          >
            <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
              <ul className="mt-4 grid gap-2">
                {orderedIds.map((danceId, i) => {
                  const d = dancesById.get(danceId);
                  if (!d) return null;
                  // Lookup by dance_id rather than array index — cascade
                  // may be shorter than orderedIds if a dance was deleted
                  // server-side since page load.
                  const cascadeRow = cascade.find((c) => c.dance_id === danceId);
                  return (
                    <SortableLineupItem
                      key={danceId}
                      id={danceId}
                      dance={d}
                      sequenceIndex={i + 1}
                      computedStartTime={cascadeRow?.computed_start_time ?? null}
                      hasConflict={conflictedDanceIds.has(danceId)}
                      castCount={(castByDance.get(danceId) ?? []).length}
                      onRemove={(id) => {
                        void removeFromLineup
                          .mutateAsync({ show_id: showId, dance_id: id })
                          .catch((e) =>
                            toast({
                              title: 'Remove failed',
                              description: e instanceof Error ? e.message : 'Unknown error',
                              variant: 'error',
                            }),
                          );
                      }}
                      onOpenCast={(id) => setCastModalDanceId(id)}
                    />
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {dancesNotInLineup.length > 0 && (
        <section aria-labelledby="add-dance-heading">
          <h2 id="add-dance-heading" className="text-[var(--text-xl)]">
            Add a dance
          </h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {dancesNotInLineup.map((d) => (
              <li key={d.id} className="surface flex items-center justify-between p-3">
                <span>
                  <span className="font-medium">{d.name}</span>
                  <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                    {d.style} · {Math.floor(d.length_seconds / 60)}:
                    {String(d.length_seconds % 60).padStart(2, '0')}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void addToLineup.mutateAsync({ show_id: showId, dance_id: d.id }).catch((e) =>
                      toast({
                        title: 'Add failed',
                        description: e instanceof Error ? e.message : 'Unknown error',
                        variant: 'error',
                      }),
                    );
                  }}
                >
                  Add to show
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CastModal
        open={castModalDanceId !== null}
        onClose={() => setCastModalDanceId(null)}
        danceId={castModalDanceId}
        danceName={castModalDance?.name}
      />
    </div>
  );
}

function UndoButton({ showId }: { showId: string }) {
  const removals = useRecentLineupRemovals(showId);
  const restore = useRestoreLastRemoval();
  const recent = removals.data?.[0];

  async function handleUndo() {
    try {
      const restored = await restore.mutateAsync(showId);
      if (!restored) {
        toast({
          title: 'Nothing to restore',
          description: 'No recent removals on this show.',
        });
        return;
      }
      toast({
        title: 'Restored',
        description: 'Dance added back to the end of the lineup.',
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: 'Undo failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  if (!recent) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => void handleUndo()}
      disabled={restore.isPending}
      aria-label="Undo last lineup removal"
    >
      {restore.isPending ? 'Restoring…' : '↶ Undo last removal'}
    </Button>
  );
}
